import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, HostListener, ElementRef } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RULE_SETS } from '@edtech-ui/core/constants/recuritment-constants/proctoring-constants/ruleSets';
import { GetProctoringRuleset } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-rulesets';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, tap } from 'rxjs';
import { GetProctoringRulesetsPagedPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-rulesets';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { ProctoringConfigValidationUtils } from '@edtech-ui/core/utils/proctoring-dashboard/proctoring-config';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
	selector: 'app-proctoring-add-assessment-dialog',
	standalone: true,
	imports: [FormsModule, CommonModule, ReactiveFormsModule, HasPermissionDirective, MatTooltipModule],
	templateUrl: './proctoring-add-assessment-dialog.component.html',
	styleUrl: './proctoring-add-assessment-dialog.component.scss'
})
export class ProctoringAddAssessmentDialogComponent implements OnInit {


	ruleSets = RULE_SETS;
	isEdit = false;
	assessmentForm: FormGroup;
	ruleData: GetProctoringRuleset[] = [];
	pageSize = 10;
	total = 0;
	isLoading = false;
	searchText: string = '';
	currentPage = 1;
	totalPages = 0;
	companyId = '';
	PermissionForStudents = PermissionForStudents;
	dropdownOpen = false;
	selectedRuleName = '';
	selectedRuleId = '';


	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		private dialogRef: MatDialogRef<ProctoringAddAssessmentDialogComponent>,
		private proctoringService: ProctoringService,
		private toastr: ToastrService,
		private eRef: ElementRef
	) {
		this.assessmentForm = new FormGroup({
			companyId: new FormControl(this.companyId),
			proctoringEvaluationOn: new FormControl('ASSESSMENT'),
			proctoringEvaluationOnId: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			evaluationTitle: new FormControl('', [Validators.required, ProctoringConfigValidationUtils.configurationNameValidator()]),
			evaluationDescription: new FormControl('', [ProctoringConfigValidationUtils.proctoringDescriptionValidator()]),
			ruleSetId: new FormControl('', [Validators.required])
		});
	}


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.assessmentForm.patchValue({ companyId: this.companyId });
		this.isEdit = this.data?.isEdit ?? false;
		if (this.isEdit && this.data?.assessment) {
			this.selectedRuleName = this.data.assessment.ruleSetName || '';
			this.assessmentForm.patchValue({
				companyId: this.data.assessment.companyId || this.companyId,
				proctoringEvaluationOn: this.data.assessment.proctoringEvaluationOn,
				proctoringEvaluationOnId: this.data.assessment.proctoringEvaluationOnId,
				evaluationTitle: this.data.assessment.evaluationTitle,
				evaluationDescription: this.data.assessment.evaluationDescription,
				ruleSetId: this.data.assessment.ruleSetId
			});
		}
		this.getProctoringRuleset();
	}


	saveAssessment(): void {
		if (this.assessmentForm.invalid) {
			this.assessmentForm.markAllAsTouched();
			return;
		}
		this.dialogRef.close({
			isEdit: this.isEdit,
			id: this.data?.assessment?.policyBindingId,
			payload: this.assessmentForm.getRawValue()
		});
	}


	closeDialog() {
		this.dialogRef.close();
	}


	getProctoringRuleset() {
		this.isLoading = true;

		const payload: GetProctoringRulesetsPagedPayload = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters: {
				companyId: {
					equals: this.companyId
				}
			},
			sort: [
				{
					field: 'createdAt',
					order: 'DESC'
				}
			],
			all: false
		};

		if (this.searchText && this.searchText.trim() !== '') {
			payload.search = this.searchText;
		}
		this.proctoringService.getProctoringRuleset(payload).pipe(
			tap(response => {
				if (response.success) {
					this.ruleData = response.data.data;
					this.total = response.data.pagination.total;
					this.currentPage = response.data.pagination.page;
					this.pageSize = response.data.pagination.pageSize;
					this.totalPages = response.data.pagination.totalPages;
				} else {
					this.toastr.error(response.message);
				}
			}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this.toastr.error('Something went wrong. Please try again.');
				return of(null);
			})
		).subscribe()
	}

	selectRule(rule: GetProctoringRuleset) {
		this.selectedRuleId = rule.id;
		this.selectedRuleName = rule.name;

		this.assessmentForm.patchValue({
			ruleSetId: rule.id
		});

		this.dropdownOpen = false;
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringRuleset();
		}
	}

	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringRuleset();
		}
	}


	@HostListener('document:click', ['$event'])
	clickout(event: Event) {
		if (this.dropdownOpen && !this.eRef.nativeElement.querySelector('.ruleset-dropdown')?.contains(event.target)) {
			this.dropdownOpen = false;
		}
	}


}